import { A, useParams } from '@solidjs/router';
import { For, Show, createEffect, createResource, createSignal, on, onCleanup } from 'solid-js';
import { api, Customer, daypartName, NowItem, Token, Track, Zone, ZoneSchedule, fmtDur, fmtTime } from '../api';
import { CopyField, ErrorLine, PageHeader, StatusPill, useAction } from '../ui';
import MixEditor from '../../../shared/MixEditor';
import ScheduleEditor from './ScheduleEditor';

type Now = { zone: { status: string }; follows: { id: string; name: string } | null; schedule: ZoneSchedule; now: NowItem; next: NowItem };

const SUGGESTED = ['morning', 'afternoon', 'evening', 'late-night', 'weekend'];
const slug = (label: string) => label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').replace(/^[^a-z]+/, '').slice(0, 32);
const clock = (iso: string) => new Date(iso).toLocaleTimeString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
type Issued = { id: string; token: string; hls_url: string; icecast_url: string; player_url: string };

export default function ZonePage() {
  const p = useParams();
  const [zone, { mutate: setZone }] = createResource(() => p.id, (id) => api<Zone>(`/zones/${id}`));
  const [customer] = createResource(() => zone()?.customer_id, (id) => api<Customer>(`/customers/${id}`));
  const [now, { refetch: refetchNow }] = createResource(() => p.id, (id) => api<Now>(`/zones/${id}/now`));
  const [library] = createResource(() => api<{ items: Track[] }>('/library/tracks?status=published&limit=500'));
  const [tokens, { refetch: refetchTokens }] = createResource(() => p.id, (id) => api<Token[]>(`/zones/${id}/tokens`));
  const [log] = createResource(() => p.id, (id) => api<{ items: any[] }>(`/zones/${id}/playlog?limit=50`));
  const [siblings] = createResource(() => zone()?.customer_id, (id) => api<Zone[]>(`/customers/${id}/zones`));
  const timer = setInterval(() => refetchNow(), 5000);
  onCleanup(() => clearInterval(timer));

  const admin = () => ['operator', 'partner_admin', 'customer_admin'].includes(customer()?.role ?? '');
  const ctl = useAction();
  const pl = useAction();
  const tk = useAction();
  const [issued, setIssued] = createSignal<Issued | null>(null);
  const [label, setLabel] = createSignal('');
  const [draft, setDraft] = createSignal<string[]>([]);
  const [q, setQ] = createSignal('');
  const [ceiling, setCeiling] = createSignal(100);
  const [tab, setTab] = createSignal<'mix' | 'manual' | null>(null);
  const musicTab = () => tab() ?? (zone()?.playlist_kind === 'manual' ? 'manual' : 'mix');
  const listen = useAction();
  const src = useAction();
  const dpa = useAction();
  const [daypart, setDaypart] = createSignal('default');
  const [newDp, setNewDp] = createSignal<string | null>(null); // a daypart being created (no mix saved yet)
  const [dpName, setDpName] = createSignal('');
  createEffect(on(() => p.id, () => { setDaypart('default'); setNewDp(null); setTab(null); }, { defer: true }));
  const dayparts = () => ['default', ...Object.keys(zone()?.genre_mix ?? {}).filter((d) => d !== 'default'), ...(newDp() ? [newDp()!] : [])];
  const leaders = () => (siblings() ?? []).filter((z) => z.id !== p.id && !z.follows_zone_id && z.status !== 'disabled');
  const leaderName = () => siblings()?.find((z) => z.id === zone()?.follows_zone_id)?.name ?? '…';
  const setSource = (leader: string) => src.run(async () => {
    setZone(await api<Zone>(`/zones/${p.id}`, { method: 'PATCH', json: { follows_zone_id: leader || null } }));
    refetchNow();
  });
  const startDaypart = (e: Event) => {
    e.preventDefault();
    const dp = slug(dpName());
    if (!dp) return;
    if (!dayparts().includes(dp)) setNewDp(dp);
    setDaypart(dp);
    setDpName('');
  };
  const removeDaypart = (dp: string) => dpa.run(async () => {
    setZone(await api<Zone>(`/zones/${p.id}/dayparts/${dp}`, { method: 'DELETE' }));
    setDaypart('default');
    refetchNow();
  });
  const scheduleLine = () => {
    const n = now();
    if (!n?.schedule || n.schedule.always_open) return 'Open around the clock';
    const until = n.schedule.next_change_at ? clock(n.schedule.next_change_at) : null;
    if (!n.schedule.open) return until ? `Opens ${until}` : 'Closed';
    return `Open · ${daypartName(n.schedule.daypart ?? 'default')} mix${until ? ` until ${until}` : ''}`;
  };
  createEffect(() => { if (zone()) { setDraft(zone()!.playlist_kind === 'manual' ? zone()!.playlist : []); setCeiling(zone()!.volume_ceiling); } });

  const byId = (id: string) => library()?.items.find((t) => t.id === id);
  const dirty = () => JSON.stringify(draft()) !== JSON.stringify(zone()?.playlist_kind === 'manual' ? zone()!.playlist : []);
  const filtered = () => (library()?.items ?? []).filter((t) => `${t.title} ${t.artist}`.toLowerCase().includes(q().toLowerCase()));
  const move = (i: number, d: number) => {
    const a = [...draft()];
    const j = i + d;
    if (j < 0 || j >= a.length) return;
    [a[i], a[j]] = [a[j], a[i]];
    setDraft(a);
  };

  const [ctlDone, setCtlDone] = createSignal('');
  const control = (action: string) => ctl.run(async () => {
    setCtlDone('');
    const r = await api<{ on_stream_at: string }>(`/zones/${p.id}/control`, { method: 'POST', json: { action } });
    const at = new Date(r.on_stream_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
    setCtlDone({ pause: `Paused: web players go quiet within 5 s, the stream turns silent at ${at}.`,
      resume: `Resumed: music is back on the stream at ${at}.`,
      skip: `Skipped: the next track starts on the stream at ${at}.` }[action] ?? '');
    refetchNow();
  });
  const savePlaylist = () => pl.run(async () => {
    setZone(await api<Zone>(`/zones/${p.id}/playlist`, { method: 'PUT', json: { track_ids: draft() } }));
    refetchNow();
  });
  const saveCeiling = () => pl.run(async () => setZone(await api<Zone>(`/zones/${p.id}`, { method: 'PATCH', json: { volume_ceiling: ceiling() } })));
  const issue = (e: Event) => {
    e.preventDefault();
    tk.run(async () => { setIssued(await api<Issued>(`/zones/${p.id}/tokens`, { method: 'POST', json: { label: label() || null } })); setLabel(''); refetchTokens(); });
  };

  // Open the tab synchronously (popup blockers), then point it at a fresh 12-hour web-player token.
  const openPlayer = () => {
    const w = window.open('about:blank', '_blank');
    listen.run(async () => {
      try {
        const r = await api<{ url: string }>(`/zones/${p.id}/web-player`, { method: 'POST' });
        if (w) w.location.href = r.url; else location.href = r.url;
        refetchTokens();
      } catch (e) { w?.close(); throw e; }
    });
  };

  const expired = (t: { expires_at: string | null }) => !!t.expires_at && Date.parse(t.expires_at.endsWith('Z') ? t.expires_at : t.expires_at + 'Z') < Date.now();
  const deadTokens = () => (tokens() ?? []).filter((t) => t.revoked_at || expired(t)).length;
  const nowLine = (it: NowItem) => !it ? '—' : it.track ? `${it.track.title} — ${it.track.artist}` : it.kind === 'silence' ? 'Silence' : it.kind;

  return (
    <>
      <PageHeader
        crumbs={[{ href: '/customers', label: 'Customers' }, { href: `/customers/${zone()?.customer_id}`, label: customer()?.name ?? '…' }, { label: 'Zone' }]}
        title={zone()?.name ?? '…'} />

      <div class="grid gap-6 xl:grid-cols-3">
        <section class="card-z border-night bg-night text-static xl:col-span-2" aria-label="Now playing">
          <div class="flex flex-wrap items-center gap-3">
            <h2 class="wordmark text-xl">Now playing</h2>
            <StatusPill tone={now()?.zone.status === 'active' && now()?.schedule?.open !== false ? 'ok' : 'warn'}>
              <span class="text-static">{now()?.zone.status === 'paused' ? 'Paused' : now()?.schedule?.open === false ? 'Closed' : 'On schedule'}</span>
            </StatusPill>
            <span class="text-sm text-lavender">{scheduleLine()}</span>
          </div>
          <Show when={now()?.follows}><p class="mt-2 text-sm text-mist">Plays the music of <b class="text-static">{now()!.follows!.name}</b>; pause and skip act on that zone too.</p></Show>
          <p class="mt-4 text-2xl font-bold" aria-live="polite">{nowLine(now()?.now ?? null)}</p>
          <p class="mt-1 text-lavender">Next: {nowLine(now()?.next ?? null)}</p>
          <div class="mt-5 flex flex-wrap gap-2">
            <Show when={now()?.zone.status === 'paused'} fallback={<button class="btn btn-sm border-night-3 bg-night-2 text-static" disabled={ctl.busy()} onClick={() => control('pause')}>Pause zone</button>}>
              <button class="btn btn-sm btn-accent" disabled={ctl.busy()} onClick={() => control('resume')}>Resume</button>
            </Show>
            <button class="btn btn-sm border-night-3 bg-night-2 text-static" disabled={ctl.busy() || now()?.zone.status !== 'active'} onClick={() => control('skip')}>Skip track</button>
            <button class="btn btn-sm btn-primary sm:ml-auto" disabled={listen.busy()} onClick={openPlayer}>Listen in browser</button>
          </div>
          <ErrorLine error={listen.error} />
          <Show when={ctlDone()}><p class="mt-3 text-sm text-static" role="status">{ctlDone()}</p></Show>
          <p class="mt-3 text-xs text-mist">Changes reach the stream about 12 seconds ahead; players hear it another 10–20 seconds behind.</p>
          <ErrorLine error={ctl.error} />
        </section>

        <section class="card-z" aria-label="Settings">
          <h2 class="wordmark text-xl">Volume ceiling</h2>
          <p class="text-sm text-ink-3">Web players cannot go louder than this.</p>
          <div class="mt-3 flex items-center gap-3">
            <input type="range" class="range range-primary range-sm" min="0" max="100" value={ceiling()} disabled={!admin()}
              onInput={(e) => setCeiling(Number(e.currentTarget.value))} aria-label="Volume ceiling" />
            <span class="mono w-12">{ceiling()}%</span>
          </div>
          <Show when={admin() && ceiling() !== zone()?.volume_ceiling}>
            <button class="btn btn-sm btn-primary mt-3" onClick={saveCeiling}>Save</button>
          </Show>

          <h2 class="wordmark mt-6 text-xl">Music source</h2>
          <Show when={(zone()?.followers.length ?? 0) > 0} fallback={<>
            <p class="text-sm text-ink-3">Follow another zone to play exactly the same music, in sync.</p>
            <select class="select select-sm select-bordered mt-3 w-full" aria-label="Music source" disabled={!admin() || src.busy()}
              value={zone()?.follows_zone_id ?? ''} onChange={(e) => setSource(e.currentTarget.value)}>
              <option value="">Its own music</option>
              <For each={leaders()}>{(z) => <option value={z.id}>Follow {z.name}</option>}</For>
            </select>
          </>}>
            <p class="text-sm text-ink-3">Followed by {zone()!.followers.map((f) => f.name).join(', ')}: they play this zone's music, so it can't follow another zone itself.</p>
          </Show>
          <ErrorLine error={src.error} />
        </section>
      </div>

      <Show when={zone()?.follows_zone_id}>
        <section class="card-z mt-6" aria-label="Music">
          <h2 class="wordmark text-xl">Music</h2>
          <p class="mt-2 text-sm">This zone follows <A class="link" href={`/zones/${zone()!.follows_zone_id}`}>{leaderName()}</A>: its genre mix, opening hours
            and playlist come from there. Choose "Its own music" under Music source to set them here again.</p>
        </section>
      </Show>
      <Show when={zone() && !zone()!.follows_zone_id}>
      <section class="card-z mt-6" aria-label="Music">
        <div class="flex flex-wrap items-center gap-3">
          <h2 class="wordmark text-xl">Music</h2>
          <div role="tablist" class="tabs tabs-boxed tabs-sm">
            <button role="tab" class={`tab ${musicTab() === 'mix' ? 'tab-active' : ''}`} aria-selected={musicTab() === 'mix'} onClick={() => setTab('mix')}>Genre mix</button>
            <button role="tab" class={`tab ${musicTab() === 'manual' ? 'tab-active' : ''}`} aria-selected={musicTab() === 'manual'} onClick={() => setTab('manual')}>Hand-picked</button>
          </div>
          <span class="text-sm text-ink-3">Playing now: {zone()?.playlist_kind === 'auto' ? 'automatic playlist from the genre mix' : zone()?.playlist_kind === 'manual' ? 'hand-picked playlist' : 'nothing set (silence)'}</span>
        </div>
        <Show when={musicTab() === 'mix' && zone()}>
          <div class="mt-4 flex flex-wrap items-center gap-2" role="tablist" aria-label="Dayparts">
            <For each={dayparts()}>
              {(dp) => (
                <button role="tab" aria-selected={daypart() === dp} onClick={() => setDaypart(dp)}
                  class={`btn btn-sm rounded-full ${daypart() === dp ? 'btn-primary' : 'btn-ghost border border-line'}`}>
                  {daypartName(dp)}{dp === newDp() ? ' (new)' : ''}
                </button>
              )}
            </For>
            <Show when={admin() && dayparts().length < 9}>
              <form class="flex items-center gap-1" onSubmit={startDaypart}>
                <input class="input input-sm input-bordered w-36" placeholder="Add daypart…" list="daypart-suggestions" value={dpName()}
                  onInput={(e) => setDpName(e.currentTarget.value)} aria-label="New daypart name" />
                <datalist id="daypart-suggestions"><For each={SUGGESTED.filter((d) => !dayparts().includes(d))}>{(d) => <option value={daypartName(d)} />}</For></datalist>
                <button class="btn btn-ghost btn-sm" disabled={!slug(dpName())}>Add</button>
              </form>
            </Show>
          </div>
          <p class="mt-2 text-sm text-ink-3">
            <Show when={daypart() === 'default'} fallback={<>The {daypartName(daypart())} mix plays during the opening hours you give it below.</>}>
              The default mix plays whenever the opening hours don't name another daypart (and around the clock without opening hours).
            </Show>
          </p>
          <div class="mt-4"><MixEditor admin={admin()}
            mix={zone()!.genre_mix?.[daypart()] ?? []}
            constraints={(daypart() === 'default' ? zone()!.constraints : zone()!.daypart_constraints?.[daypart()]) ?? {}}
            previewPath={`/zones/${zone()!.id}/playlist/preview`}
            previewBody={(genre_mix, constraints) => ({ genre_mix: { default: genre_mix }, constraints })}
            saveLabel={!zone()!.genre_mix?.[daypart()]?.length && daypart() !== 'default' ? 'Create daypart' : 'Save mix'}
            alwaysSavable={!zone()!.genre_mix?.[daypart()]?.length || (daypart() === 'default' && zone()!.playlist_kind !== 'auto')}
            notice={<Show when={daypart() === 'default' && zone()!.playlist_kind === 'manual'}>
              <p class="rounded-lg border border-amber bg-static px-3 py-2 text-sm">This zone plays a hand-built playlist. Saving a mix switches it to an automatic playlist.</p>
            </Show>}
            onSave={async (genre_mix, constraints) => {
              const z = await api<Zone>(`/zones/${zone()!.id}/dayparts/${daypart()}`, { method: 'PUT', json: { genre_mix, constraints } });
              setZone(z); if (daypart() === newDp()) setNewDp(null); refetchNow();
            }} /></div>
          <Show when={admin() && daypart() !== 'default'}>
            <div class="mt-4 border-t border-line pt-3">
              <Show when={daypart() === newDp()} fallback={
                <button class="btn btn-ghost btn-sm text-error" disabled={dpa.busy()} onClick={() => removeDaypart(daypart())}>Remove {daypartName(daypart())} daypart</button>}>
                <button class="btn btn-ghost btn-sm" onClick={() => { setNewDp(null); setDaypart('default'); }}>Discard new daypart</button>
              </Show>
              <ErrorLine error={dpa.error} />
            </div>
          </Show>
        </Show>
        <Show when={musicTab() === 'manual'}>
        <div class="mt-4 flex flex-wrap items-center gap-3">
          <span class="text-sm text-ink-3">Plays in this order and loops. Saving replaces the automatic playlist until a mix is saved again.</span>
          <Show when={admin() && dirty()}>
            <button class="btn btn-sm btn-primary ml-auto" disabled={pl.busy() || draft().length === 0} onClick={savePlaylist}>Save playlist</button>
          </Show>
        </div>
        <ErrorLine error={pl.error} />
        <div class="mt-4 grid gap-6 lg:grid-cols-2">
          <div>
            <h3 class="th mb-2">In this zone ({draft().length} · {fmtDur(draft().reduce((s, id) => s + (byId(id)?.duration_ms ?? 0), 0))})</h3>
            <ol class="flex flex-col gap-1">
              <For each={draft()} fallback={<li class="text-sm text-ink-3">Empty: the zone plays silence.</li>}>
                {(id, i) => (
                  <li class="flex items-center gap-2 rounded-lg border border-line px-3 py-2">
                    <span class="mono w-6 text-ink-3">{i() + 1}</span>
                    <span class="min-w-0 flex-1 truncate"><b>{byId(id)?.title ?? id.slice(0, 8)}</b> <span class="text-ink-3">{byId(id)?.artist}</span></span>
                    <span class="mono text-ink-3">{fmtDur(byId(id)?.duration_ms ?? 0)}</span>
                    <Show when={admin()}>
                      <button class="btn btn-ghost btn-xs" aria-label="Move up" onClick={() => move(i(), -1)}>↑</button>
                      <button class="btn btn-ghost btn-xs" aria-label="Move down" onClick={() => move(i(), 1)}>↓</button>
                      <button class="btn btn-ghost btn-xs" aria-label="Remove" onClick={() => setDraft(draft().filter((_, k) => k !== i()))}>✕</button>
                    </Show>
                  </li>
                )}
              </For>
            </ol>
          </div>
          <Show when={admin()}>
            <div>
              <h3 class="th mb-2">Library (published)</h3>
              <input class="input input-sm input-bordered mb-2 w-full" placeholder="Search title or artist" value={q()} onInput={(e) => setQ(e.currentTarget.value)} aria-label="Search library" />
              <div class="flex max-h-80 flex-col gap-1 overflow-y-auto">
                <For each={filtered()}>
                  {(t) => (
                    <button class="flex items-center gap-2 rounded-lg px-3 py-1.5 text-left hover:bg-static" onClick={() => setDraft([...draft(), t.id])}>
                      <span class="min-w-0 flex-1 truncate"><b>{t.title}</b> <span class="text-ink-3">{t.artist}</span></span>
                      <span class="mono text-ink-3">{fmtDur(t.duration_ms)}</span>
                      <span class="text-lg font-bold text-magenta" aria-hidden="true">+</span>
                    </button>
                  )}
                </For>
              </div>
              <button class="btn btn-ghost btn-xs mt-2" onClick={() => setDraft([...draft(), ...filtered().map((t) => t.id)])}>Add all shown</button>
            </div>
          </Show>
        </div>
        </Show>
      </section>

      <section class="card-z mt-6" aria-label="Opening hours">
        <h2 class="wordmark text-xl">Opening hours</h2>
        <div class="mt-4"><ScheduleEditor zone={zone()!} admin={admin()} onSaved={(z) => { setZone(z); refetchNow(); }} /></div>
      </section>
      </Show>

      <Show when={admin()}>
        <section class="card-z mt-6" aria-label="Player endpoint">
          <h2 class="wordmark text-xl">Player endpoint</h2>
          <p class="text-sm text-ink-3">One token per device. Point a streaming device at the URL, or open the web player. Rotating keeps the old token working for 24 hours; revoking one device never breaks the others.</p>
          <form class="mt-4 flex flex-wrap gap-2" onSubmit={issue}>
            <input class="input input-sm input-bordered" placeholder="Label, e.g. Sonos in shop" value={label()} onInput={(e) => setLabel(e.currentTarget.value)} aria-label="Token label" />
            <button class="btn btn-sm btn-primary" disabled={tk.busy()}>New endpoint token</button>
          </form>
          <ErrorLine error={tk.error} />
          <Show when={issued()}>
            <div class="mt-4 flex flex-col gap-2 rounded-xl border-2 border-amber bg-static p-4">
              <p class="text-sm font-semibold">Shown once. Copy these now.</p>
              <span class="th">HLS</span><CopyField label="HLS URL" value={issued()!.hls_url} />
              <span class="th">Icecast-compatible MP3</span><CopyField label="MP3 URL" value={issued()!.icecast_url} />
              <span class="th">Web player</span><CopyField label="Web player URL" value={issued()!.player_url} />
              <a class="btn btn-sm btn-secondary self-start" href={issued()!.player_url} target="_blank" rel="noopener">Open web player</a>
            </div>
          </Show>
          <Show when={deadTokens() > 0}>
            <div class="mt-4 flex items-center gap-3 text-sm text-ink-3">
              <span>{deadTokens()} revoked or expired</span>
              <button class="btn btn-ghost btn-xs" disabled={tk.busy()} onClick={() => tk.run(async () => { await api(`/zones/${p.id}/tokens/purge`, { method: 'POST' }); refetchTokens(); })}>Remove them</button>
            </div>
          </Show>
          <table class="table mt-4">
            <thead><tr><th class="th">Label</th><th class="th">Issued</th><th class="th">State</th><th /></tr></thead>
            <tbody>
              <For each={tokens()}>
                {(t) => (
                  <tr>
                    <td>{t.label ?? '—'} <Show when={t.device_id}><span class="text-xs text-ink-3">(paired device)</span></Show></td>
                    <td class="text-sm text-ink-3">{fmtTime(t.issued_at)}</td>
                    <td>
                      <Show when={t.revoked_at} fallback={<Show when={t.expires_at} fallback={<StatusPill tone="ok">active</StatusPill>}>
                        <Show when={!expired(t)} fallback={<StatusPill tone="off">expired</StatusPill>}><StatusPill tone="warn">expires {fmtTime(t.expires_at)}</StatusPill></Show></Show>}>
                        <StatusPill tone="off">revoked</StatusPill>
                      </Show>
                    </td>
                    <td class="whitespace-nowrap text-right">
                      <Show when={!t.revoked_at}>
                        <button class="btn btn-ghost btn-xs" onClick={() => tk.run(async () => { setIssued(await api<Issued>(`/zones/${p.id}/tokens/${t.id}/rotate`, { method: 'POST' })); refetchTokens(); })}>Rotate</button>
                        <button class="btn btn-ghost btn-xs text-error" onClick={() => tk.run(async () => { await api(`/zones/${p.id}/tokens/${t.id}`, { method: 'DELETE' }); refetchTokens(); })}>Revoke</button>
                      </Show>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </section>
      </Show>

      <section class="card-z mt-6" aria-label="Play log">
        <div class="flex items-center gap-3">
          <h2 class="wordmark text-xl">Play log</h2>
          <button class="btn btn-ghost btn-xs ml-auto" onClick={async () => {
            const r = await api<Response>(`/zones/${p.id}/playlog?limit=10000`, { raw: true, headers: { Accept: 'text/csv' } });
            const url = URL.createObjectURL(await r.blob());
            const a = document.createElement('a'); a.href = url; a.download = `playlog-${p.id}.csv`; a.click(); URL.revokeObjectURL(url);
          }}>Export CSV</button>
        </div>
        <table class="table table-sm mt-2">
          <thead><tr><th class="th">Played</th><th class="th">Track</th><th class="th">Duration</th></tr></thead>
          <tbody>
            <For each={log()?.items} fallback={<tr><td colspan="3" class="text-ink-3">Nothing logged yet (the log lags a minute).</td></tr>}>
              {(r) => <tr><td class="text-sm text-ink-3">{fmtTime(r.played_at)}</td><td>{r.title} <span class="text-ink-3">{r.artist}</span></td><td class="mono">{fmtDur(r.duration_ms)}</td></tr>}
            </For>
          </tbody>
        </table>
      </section>
    </>
  );
}
